// The new connection wizard's vocabulary: its four steps and their titles, what each keeps, where a
// create stands, and the rules that hold across steps — the holder's CPF is emptied whenever the
// wizard goes to the holder step or before it (so a CPF shows in clear only while it is typed), a
// newly chosen transmitter asks for every type it offers, and the consent asks for at least one.
import { OPIN_RECEIVER_VALIDITY_MONTHS_MAX } from '@finnest/power-admin-contracts/opin-receiver';
import type {
  OpinReceiverConnection,
  OpinReceiverConsentUser,
  OpinReceiverInsuranceType,
  OpinReceiverTransmitter,
} from '@finnest/power-admin-contracts/opin-receiver';
import type { ReceiverCallFailure } from '../../client/calls.ts';
import { brokenPurposeRules, isCpf, isCnpj } from '../../view/format.ts';

export const WIZARD_STEPS = ['transmitter', 'holder', 'terms', 'review'] as const;
export type WizardStepKind = (typeof WIZARD_STEPS)[number];

/** Each step's title in the stepper, and what it is about until it is done. */
export const WIZARD_STEP_TITLES: Readonly<
  Record<WizardStepKind, { readonly title: string; readonly about: string }>
> = {
  transmitter: { title: 'Transmissora', about: 'Quem envia os dados' },
  holder: { title: 'Titular', about: 'De quem são os dados' },
  terms: { title: 'Dados e finalidade', about: 'O que e para quê' },
  review: { title: 'Revisar e enviar', about: 'Conferir e autorizar' },
};

/** Where the wizard is, and what the steps before it chose. */
export type Step =
  // The transmitter the operator marked, kept when they come back to change it; null before any.
  | { readonly kind: 'transmitter'; readonly transmitter: OpinReceiverTransmitter | null }
  // `retype`: the holder was accepted before, so its CPF, emptied, is asked again.
  | {
      readonly kind: 'holder';
      readonly transmitter: OpinReceiverTransmitter;
      readonly retype: boolean;
    }
  | { readonly kind: 'terms'; readonly transmitter: OpinReceiverTransmitter }
  | { readonly kind: 'review'; readonly transmitter: OpinReceiverTransmitter }
  // A replay of a create whose authorization already started: there is no URL to resume it.
  | { readonly kind: 'already-started'; readonly id: OpinReceiverConnection['id'] };

/** A step of the wizard itself, the four the operator moves between. */
export type WizardStep = Exclude<Step, { readonly kind: 'already-started' }>;

/** The steps the wizard can go back to: every step but the last is done before it. */
export type DoneStepKind = Exclude<WizardStepKind, 'review'>;

/** The step a done step goes back to: the holder accepted before types its CPF again. */
export const stepBack = (kind: DoneStepKind, transmitter: OpinReceiverTransmitter): WizardStep => {
  if (kind === 'transmitter') return { kind, transmitter };
  if (kind === 'holder') return { kind, transmitter, retype: true };
  return { kind, transmitter };
};

/** Whose data the consent is for: a person's own, or a company's, authorised by a representative. */
export const HOLDER_KINDS = ['person', 'company'] as const;
export type HolderKind = (typeof HOLDER_KINDS)[number];

/** The holder as the form keeps it; the CNPJ only counts for a company. */
export type Holder = {
  readonly kind: HolderKind;
  readonly cpf: string;
  readonly cnpj: string;
  readonly consentUser: OpinReceiverConsentUser;
};

/** A holder whose documents could be sent: a CPF whose check digits hold, and a company's CNPJ. */
export const holderValid = ({ kind, cpf, cnpj }: Holder): boolean =>
  isCpf(cpf) && (kind === 'person' || isCnpj(cnpj));

/** The holder of another kind: a person keeps no company's CNPJ. */
export const holderOfKind = (holder: Holder, kind: HolderKind): Holder =>
  kind === 'person' ? { ...holder, kind, cnpj: '' } : { ...holder, kind };

// Whether arriving at a step empties the CPF: at the holder step or before it, it is typed again.
const EMPTIES_CPF: Readonly<Record<Step['kind'], boolean>> = {
  transmitter: true,
  holder: true,
  terms: false,
  review: false,
  'already-started': false,
};

/** The holder once the wizard is at `next`. */
export const holderAt = (next: Step, holder: Holder): Holder =>
  EMPTIES_CPF[next.kind] ? { ...holder, cpf: '' } : holder;

/** What the summary names at each step: the holder once its step was accepted, the terms once theirs. */
export const ACCEPTED: Readonly<
  Record<WizardStepKind, { readonly holder: boolean; readonly terms: boolean }>
> = {
  transmitter: { holder: false, terms: false },
  holder: { holder: false, terms: false },
  terms: { holder: true, terms: false },
  review: { holder: true, terms: true },
};

export const INITIAL_HOLDER = {
  kind: 'person',
  cpf: '',
  cnpj: '',
  consentUser: 'user',
} as const satisfies Holder;

/** The purpose the form opens with, which the operator may adjust to the request. */
export const DEFAULT_PURPOSE =
  'Consultar os dados de seguro do titular para oferecer produtos e serviços adequados ao seu perfil e às suas necessidades.';

/** The consent terms as the form keeps them; the types are the ones asked, at least one. */
export type Terms = {
  readonly types: readonly OpinReceiverInsuranceType[];
  readonly purpose: string;
  readonly validityMonths: number;
};

export const INITIAL_TERMS = {
  types: [],
  purpose: DEFAULT_PURPOSE,
  validityMonths: OPIN_RECEIVER_VALIDITY_MONTHS_MAX,
} as const satisfies Terms;

/** Terms the receiver would take: at least one type, and a purpose within its rules. */
export const termsValid = ({ types, purpose }: Terms): boolean =>
  types.length > 0 && brokenPurposeRules(purpose).length === 0;

/** The types asked once `chosen` is marked: all it offers when new, the same when marked again. */
export const typesOnChoice = (
  terms: Terms,
  marked: OpinReceiverTransmitter | null,
  chosen: OpinReceiverTransmitter,
): readonly OpinReceiverInsuranceType[] =>
  marked?.id === chosen.id ? terms.types : chosen.supportedTypes;

/** Where the create stands. */
export type Submission =
  | { readonly kind: 'editing' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'failed'; readonly failure: ReceiverCallFailure };

export const EDITING = { kind: 'editing' } as const satisfies Submission;
